import React, { useEffect, useMemo, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { useNavigate } from 'react-router-dom';
import { accountsService } from '../../services/accountsService';
import { phonesService } from '../../services/phonesService';
import { userService } from '../../services/userService';
import ResourceSidebar from '../Shared/ResourceSidebar';
import HeaderNewTabButton from '../Shared/HeaderNewTabButton';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import '../Venues/VenuesPage.css';
import '../Cards/CardsPage.css';

ModuleRegistry.registerModules([AllCommunityModule]);

const EMPTY_PHONE_FORM = {
  phone_number: '',
  owner_user_id: '',
  owner_query: '',
  provider: '',
  status: 'Active',
};
const USERNAME_COL_WIDTH = 78;
const STATUS_COL_WIDTH = 92;
const ASSIGNMENT_VENDOR_OPTIONS = [
  { value: 'dice', label: 'Dice' },
  { value: 'aspen', label: 'Belly Up Aspen' },
  { value: 'tao', label: 'Tao' },
];
const ASSIGNMENT_EMPTY_STATE = {
  phone: null,
  vendor: 'dice',
  query: '',
  account: null,
  suggestions: [],
  suggestionsOpen: false,
  suggestionsLoading: false,
  saving: false,
  error: '',
};

const PhonesPage = () => {
  const navigate = useNavigate();
  const [phones, setPhones] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_PHONE_FORM);
  const [createSaving, setCreateSaving] = useState(false);
  const [createError, setCreateError] = useState('');
  const [ownerSuggestions, setOwnerSuggestions] = useState([]);
  const [ownerSuggestionsOpen, setOwnerSuggestionsOpen] = useState(false);
  const [ownerSuggestionsLoading, setOwnerSuggestionsLoading] = useState(false);
  const [assignment, setAssignment] = useState(ASSIGNMENT_EMPTY_STATE);

  const loadPhones = async (qValue = '') => {
    setLoading(true);
    setError('');
    const result = await phonesService.listPhones({
      q: qValue || undefined,
      limit: 5000,
    });

    if (result.success) {
      setPhones(result.items || []);
      setTotalCount(result.totalCount || 0);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadPhones('');
  }, []);

  useEffect(() => {
    const ownerQuery = createForm.owner_query.trim();
    if (!showCreateModal || !ownerQuery || createForm.owner_user_id) {
      setOwnerSuggestions([]);
      setOwnerSuggestionsOpen(false);
      return undefined;
    }

    const timer = setTimeout(async () => {
      setOwnerSuggestionsLoading(true);
      const result = await userService.searchVendorUsers(ownerQuery);
      setOwnerSuggestionsLoading(false);
      if (result.success) {
        setOwnerSuggestions(result.items || []);
        setOwnerSuggestionsOpen(true);
      } else {
        setOwnerSuggestions([]);
        setOwnerSuggestionsOpen(false);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [createForm.owner_query, createForm.owner_user_id, showCreateModal]);

  useEffect(() => {
    if (
      !assignment.phone
      || !assignment.query.trim()
      || (
        assignment.account?.username
        && assignment.query.trim().toLowerCase() === assignment.account.username.toLowerCase()
      )
    ) {
      setAssignment((current) => ({
        ...current,
        suggestions: [],
        suggestionsOpen: false,
        suggestionsLoading: false,
      }));
      return undefined;
    }

    let cancelled = false;
    const timeoutId = window.setTimeout(async () => {
      setAssignment((current) => ({ ...current, suggestionsLoading: true, error: '' }));
      const result = await accountsService.listVendorAccounts(assignment.vendor, {
        includeClosed: true,
        includeIssue: true,
        q: assignment.query.trim(),
      });
      if (cancelled) return;
      setAssignment((current) => ({
        ...current,
        suggestionsLoading: false,
        suggestions: result.success ? result.items.slice(0, 8) : [],
        suggestionsOpen: result.success && !!result.items.length,
        error: result.success ? current.error : result.error,
      }));
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [assignment.account?.username, assignment.phone, assignment.query, assignment.vendor]);

  const columnDefs = useMemo(() => [
    {
      headerName: 'Phone Number',
      field: 'phone_number',
      minWidth: 180,
      flex: 1.2,
      editable: false,
      pinned: 'left',
    },
    {
      headerName: 'Owner',
      field: 'owner_username',
      minWidth: USERNAME_COL_WIDTH,
      width: USERNAME_COL_WIDTH,
      maxWidth: USERNAME_COL_WIDTH,
      editable: false,
    },
    {
      headerName: 'Provider',
      field: 'provider',
      minWidth: 130,
      editable: true,
    },
    {
      headerName: 'Status',
      field: 'status',
      minWidth: STATUS_COL_WIDTH,
      width: STATUS_COL_WIDTH,
      maxWidth: STATUS_COL_WIDTH,
      editable: true,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: ['Active', 'Closed'],
      },
      cellClassRules: {
        'status-cell-active': (params) => String(params.value || '').toLowerCase() === 'active',
        'status-cell-closed': (params) => String(params.value || '').toLowerCase() === 'closed',
      },
    },
    {
      headerName: 'Dice',
      field: 'dice',
      minWidth: 110,
      editable: false,
    },
    {
      headerName: 'Aspen',
      field: 'aspen',
      minWidth: 110,
      editable: false,
    },
    {
      headerName: 'Ticketmaster',
      field: 'ticketmaster',
      minWidth: 140,
      editable: false,
    },
    {
      headerName: 'Tao',
      field: 'tao',
      minWidth: 110,
      editable: false,
    },
    {
      headerName: 'Get In',
      field: 'get_in',
      minWidth: 110,
      editable: false,
    },
    {
      headerName: 'Seetickets',
      field: 'seetickets',
      minWidth: 130,
      editable: false,
    },
    {
      headerName: 'Axs',
      field: 'axs',
      minWidth: 110,
      editable: false,
    },
    {
      headerName: 'Tixr',
      field: 'tixr',
      minWidth: 110,
      editable: false,
    },
    {
      headerName: 'Assign',
      field: 'assign',
      minWidth: 105,
      width: 105,
      maxWidth: 105,
      sortable: false,
      filter: false,
      editable: false,
      cellRenderer: (params) => (
        <button
          className="cards-grid-action"
          type="button"
          onClick={() => openAssignmentModal(params.data)}
        >
          Assign
        </button>
      ),
    },
  ], []);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    floatingFilter: true,
    resizable: true,
  }), []);

  const normalizeOptionalText = (value) => {
    const trimmed = String(value || '').trim();
    return trimmed || null;
  };

  const handleFilter = (event) => {
    event.preventDefault();
    loadPhones(query.trim());
  };

  const openAssignmentModal = (phone) => {
    setAssignment({
      ...ASSIGNMENT_EMPTY_STATE,
      phone,
    });
  };

  const closeAssignmentModal = () => {
    if (assignment.saving) return;
    setAssignment(ASSIGNMENT_EMPTY_STATE);
  };

  const updateAssignment = (field, value) => {
    setAssignment((current) => {
      const next = {
        ...current,
        [field]: value,
        error: '',
      };
      if (field === 'vendor') {
        next.query = '';
        next.account = null;
        next.suggestions = [];
        next.suggestionsOpen = false;
      }
      if (field === 'query') {
        next.account = null;
      }
      return next;
    });
  };

  const selectAssignmentAccount = (account) => {
    setAssignment((current) => ({
      ...current,
      account,
      query: account.username || '',
      suggestions: [],
      suggestionsOpen: false,
      error: '',
    }));
  };

  const saveAssignment = async () => {
    if (!assignment.phone || !assignment.account) {
      setAssignment((current) => ({ ...current, error: 'Select a vendor account first.' }));
      return;
    }

    setAssignment((current) => ({ ...current, saving: true, error: '' }));
    const result = await accountsService.manageVendorPhone(
      assignment.vendor,
      assignment.account.user_id,
      { phone_id: assignment.phone.phone_id },
    );

    if (!result.success) {
      setAssignment((current) => ({ ...current, saving: false, error: result.error }));
      return;
    }

    setAssignment(ASSIGNMENT_EMPTY_STATE);
    setMessage(result.message || 'Phone assigned.');
    setError('');
    await loadPhones(query.trim());
  };

  const openCreateModal = () => {
    setCreateForm(EMPTY_PHONE_FORM);
    setCreateError('');
    setOwnerSuggestions([]);
    setOwnerSuggestionsOpen(false);
    setShowCreateModal(true);
  };

  const closeCreateModal = () => {
    if (createSaving) return;
    setShowCreateModal(false);
    setCreateError('');
  };

  const handleCreateFormChange = (field, value) => {
    setCreateForm((current) => ({
      ...current,
      [field]: value,
      ...(field === 'owner_query' ? { owner_user_id: '' } : {}),
    }));
  };

  const selectOwner = (owner) => {
    setCreateForm((current) => ({
      ...current,
      owner_user_id: owner.user_id,
      owner_query: owner.username,
    }));
    setOwnerSuggestions([]);
    setOwnerSuggestionsOpen(false);
  };

  const handleCreatePhone = async (event) => {
    event.preventDefault();
    const phoneNumber = createForm.phone_number.trim();

    if (!phoneNumber || !createForm.owner_user_id) {
      setCreateError('Phone Number and Owner are required.');
      return;
    }

    setCreateSaving(true);
    setCreateError('');
    const result = await phonesService.createPhone({
      phone_number: phoneNumber,
      owner_user_id: createForm.owner_user_id,
      provider: normalizeOptionalText(createForm.provider),
      status: normalizeOptionalText(createForm.status),
    });
    setCreateSaving(false);

    if (!result.success) {
      setCreateError(result.error);
      return;
    }

    setPhones((current) => [result.item, ...current]);
    setTotalCount((current) => current + 1);
    setMessage(result.message || 'Phone created.');
    setError('');
    setShowCreateModal(false);
  };

  const handleCellValueChanged = async (params) => {
    const { data, colDef, oldValue, newValue, node } = params;
    const field = colDef.field;

    const readOnlyFields = ['phone_number', 'owner_username', 'dice', 'aspen', 'ticketmaster', 'tao', 'get_in', 'seetickets', 'axs', 'tixr'];
    if (!field || oldValue === newValue || readOnlyFields.includes(field)) {
      return;
    }

    const result = await phonesService.updatePhone(data.phone_id, {
      [field]: newValue === '' ? null : newValue,
    });

    if (!result.success) {
      node.setDataValue(field, oldValue);
      setError(result.error);
      return;
    }

    setError('');
    setMessage(result.message || 'Phone updated.');
    setPhones((current) => current.map((item) => (
      item.phone_id === result.item.phone_id ? result.item : item
    )));
    node.setData(result.item);
  };

  return (
    <div className="venues-page phones-page resource-page-with-sidebar">
      <ResourceSidebar />
      <header className="venues-header">
        <div className="venues-header-content">
          <HeaderNewTabButton />
          <h1>Phone Numbers</h1>
          <div className="venues-header-actions">
            <button className="btn btn-primary" type="button" onClick={openCreateModal}>
              Add Phone
            </button>
            <button className="btn btn-secondary" type="button" onClick={() => navigate('/user')}>
              Back to Dashboard
            </button>
          </div>
        </div>
      </header>

      <main className="venues-main">
        <form className="venues-filter-form" onSubmit={handleFilter}>
          <div className="form-group">
            <label htmlFor="phones-query">Search</label>
            <input
              id="phones-query"
              type="text"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="phone, owner, provider, vendor assignment, status..."
            />
          </div>
          <button className="btn btn-primary" type="submit">
            Apply
          </button>
        </form>

        <div className="venues-summary">
          <strong>Showing:</strong> {phones.length} phone numbers
          {!!totalCount && <span className="venues-summary-tag">Total: {totalCount}</span>}
        </div>

        {error && <div className="notification error">{error}</div>}
        {message && <div className="notification success">{message}</div>}

        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading phone numbers...</p>
          </div>
        ) : (
          <div className="venues-grid-shell">
            <div className="ag-theme-quartz-dark venues-ag-grid">
              <AgGridReact
                theme="legacy"
                rowData={phones}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                animateRows={true}
                alwaysShowHorizontalScroll={true}
                pagination={true}
                paginationPageSize={100}
                getRowId={(params) => params.data.phone_id}
                onCellValueChanged={handleCellValueChanged}
              />
            </div>
          </div>
        )}

        {showCreateModal && (
          <div className="venues-modal-backdrop" onMouseDown={closeCreateModal}>
            <section className="venues-modal" onMouseDown={(event) => event.stopPropagation()}>
              <div className="venues-modal-header">
                <h2>Add Phone</h2>
                <button className="venues-modal-close" type="button" onClick={closeCreateModal} disabled={createSaving}>
                  x
                </button>
              </div>

              <form className="venues-modal-form" onSubmit={handleCreatePhone}>
                <div className="form-group">
                  <label htmlFor="phone-number">Phone Number</label>
                  <input
                    id="phone-number"
                    type="text"
                    value={createForm.phone_number}
                    onChange={(event) => handleCreateFormChange('phone_number', event.target.value)}
                    disabled={createSaving}
                    required
                  />
                </div>

                <div className="form-group cards-owner-field">
                  <label htmlFor="phone-owner">Owner</label>
                  <input
                    id="phone-owner"
                    type="text"
                    value={createForm.owner_query}
                    onChange={(event) => handleCreateFormChange('owner_query', event.target.value)}
                    disabled={createSaving}
                    autoComplete="off"
                    required
                  />
                  {ownerSuggestionsLoading && <div className="cards-owner-hint">Searching...</div>}
                  {ownerSuggestionsOpen && !!ownerSuggestions.length && (
                    <div className="cards-owner-suggestions">
                      {ownerSuggestions.map((owner) => (
                        <button key={owner.user_id} type="button" onClick={() => selectOwner(owner)}>
                          <strong>{owner.username}</strong>
                          <span>{owner.email || owner.name || ''}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="phone-provider">Provider</label>
                  <input
                    id="phone-provider"
                    type="text"
                    value={createForm.provider}
                    onChange={(event) => handleCreateFormChange('provider', event.target.value)}
                    disabled={createSaving}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone-status">Status</label>
                  <select
                    id="phone-status"
                    value={createForm.status}
                    onChange={(event) => handleCreateFormChange('status', event.target.value)}
                    disabled={createSaving}
                  >
                    <option value="Active">Active</option>
                    <option value="Closed">Closed</option>
                  </select>
                </div>

                {createError && <div className="notification error">{createError}</div>}

                <div className="venues-modal-actions">
                  <button className="btn btn-secondary" type="button" onClick={closeCreateModal} disabled={createSaving}>
                    Cancel
                  </button>
                  <button className="btn btn-primary" type="submit" disabled={createSaving}>
                    {createSaving ? 'Saving...' : 'Save Phone'}
                  </button>
                </div>
              </form>
            </section>
          </div>
        )}

        {assignment.phone && (
          <div className="venues-modal-backdrop" onMouseDown={closeAssignmentModal}>
            <section className="venues-modal cards-assignment-modal" onMouseDown={(event) => event.stopPropagation()}>
              <div className="venues-modal-header">
                <h2>Assign Phone</h2>
                <button className="venues-modal-close" type="button" onClick={closeAssignmentModal} disabled={assignment.saving}>
                  x
                </button>
              </div>

              <div className="venues-modal-form">
                <div className="cards-assignment-card">
                  <span>Phone</span>
                  <strong>{assignment.phone.phone_number}</strong>
                </div>

                <div className="form-group">
                  <label htmlFor="phones-assignment-vendor">Vendor</label>
                  <select
                    id="phones-assignment-vendor"
                    value={assignment.vendor}
                    onChange={(event) => updateAssignment('vendor', event.target.value)}
                    disabled={assignment.saving}
                  >
                    {ASSIGNMENT_VENDOR_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group cards-owner-field">
                  <label htmlFor="phones-assignment-account">Vendor Account</label>
                  <input
                    id="phones-assignment-account"
                    type="text"
                    value={assignment.query}
                    onChange={(event) => updateAssignment('query', event.target.value)}
                    placeholder="Search username, email, or current login..."
                    disabled={assignment.saving}
                    autoComplete="off"
                  />
                  {assignment.suggestionsLoading ? <div className="cards-owner-hint">Searching...</div> : null}
                  {assignment.suggestionsOpen && !!assignment.suggestions.length ? (
                    <div className="cards-owner-suggestions">
                      {assignment.suggestions.map((account) => (
                        <button key={account.user_id} type="button" onClick={() => selectAssignmentAccount(account)}>
                          <strong>{account.username}</strong>
                          <span>{[account.name, account.email, account.login].filter(Boolean).join(' | ')}</span>
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>

                {assignment.account ? (
                  <div className="cards-assignment-current">
                    <span>Current account value</span>
                    <strong>{assignment.account.login || 'Empty'}</strong>
                  </div>
                ) : null}

                {assignment.error && <div className="notification error">{assignment.error}</div>}

                <div className="venues-modal-actions">
                  <button className="btn btn-secondary" type="button" onClick={closeAssignmentModal} disabled={assignment.saving}>
                    Cancel
                  </button>
                  <button className="btn btn-primary" type="button" onClick={saveAssignment} disabled={assignment.saving || !assignment.account}>
                    {assignment.saving ? 'Saving...' : 'Assign Phone'}
                  </button>
                </div>
              </div>
            </section>
          </div>
        )}
      </main>
    </div>
  );
};

export default PhonesPage;
